// Boutique publique AYA — standard Konan Coco (Réf 7). Structure commune, données par commerçant.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, ShoppingCart, User, MapPin, Leaf, Snowflake, Truck, Clock, PhoneCall, Heart, Share2, MessageCircle, Flame, ArrowRight, ShieldCheck, Headphones, Plus, Users, CheckCircle2, X, Home, Bookmark, Store } from "lucide-react";
import { toast } from "sonner";
import "./boutique.css";

const API = process.env.REACT_APP_BACKEND_URL;
const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202f/g, " ")} FCFA`;
const HL_ICONS = { leaf: Leaf, snow: Snowflake, truck: Truck };

export default function BoutiquePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [cat, setCat] = useState("Tout");
  const [cart, setCart] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [followed, setFollowed] = useState(() => localStorage.getItem(`bq_follow_${slug}`) === "1");
  const [form, setForm] = useState({ name: "", phone: "", mode: "retrait" });
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);
  const [live, setLive] = useState(null);
  const [liveMsg, setLiveMsg] = useState("");
  const [reviewsData, setReviewsData] = useState(null);
  const [review, setReview] = useState({ rating: 0, comment: "", sent: false });
  const viewed = useRef(false);
  const clientId = useRef((() => { let v = localStorage.getItem("aya_client_id"); if (!v) { v = Math.random().toString(36).slice(2, 12); localStorage.setItem("aya_client_id", v); } return v; })());

  // LIVE — polling léger (état + messages + spectateurs réels via ping).
  useEffect(() => {
    let on = true;
    const tick = async () => {
      try {
        fetch(`${API}/api/boutique/${slug}/live/ping`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ client_id: clientId.current }) }).catch(() => {});
        const j = await fetch(`${API}/api/boutique/${slug}/live`).then((r) => r.json());
        if (on && j.ok) setLive(j.live);
      } catch { /* réseau */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { on = false; clearInterval(t); };
  }, [slug]);

  useEffect(() => {
    fetch(`${API}/api/boutique/${slug}/reviews`).then((r) => r.json()).then((j) => { if (j.ok) setReviewsData(j); }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const token = localStorage.getItem("aya_pro_token") || sessionStorage.getItem("aya_pro_token");
    fetch(`${API}/api/boutique/${slug}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.json())
      .then((j) => { if (j.ok) setData(j); else setErr(j.error); })
      .catch(() => setErr("network"));
    if (!viewed.current) { viewed.current = true; fetch(`${API}/api/boutique/${slug}/view`, { method: "POST" }).catch(() => {}); }
  }, [slug]);

  const shop = data?.shop;
  const products = data?.products || [];
  const shown = useMemo(() => (cat === "Tout" ? products : products.filter((p) => p.category === cat)), [cat, products]);
  const count = cart.reduce((t, l) => t + l.qty, 0);
  const total = cart.reduce((t, l) => t + l.price * l.qty, 0);

  const add = (p) => {
    setCart((c) => {
      const i = c.findIndex((l) => l.product_id === p.id);
      if (i >= 0) { const n = [...c]; n[i] = { ...n[i], qty: n[i].qty + 1 }; return n; }
      return [...c, { product_id: p.id, name: p.name, price: p.price, image: p.image, qty: 1 }];
    });
    toast.success(`${p.name} ajouté au panier`);
  };
  const addFamily = () => {
    if (!shop?.family_offer) return;
    setCart((c) => {
      const i = c.findIndex((l) => l.product_id === "family_offer");
      if (i >= 0) { const n = [...c]; n[i] = { ...n[i], qty: n[i].qty + 1 }; return n; }
      return [...c, { product_id: "family_offer", name: shop.family_offer.title, price: shop.family_offer.price, image: shop.family_offer.image, qty: 1 }];
    });
    setDrawer(true);
  };
  const setQty = (id, d) => setCart((c) => c.map((l) => (l.product_id === id ? { ...l, qty: l.qty + d } : l)).filter((l) => l.qty > 0));

  const follow = async () => {
    const next = !followed;
    setFollowed(next);
    localStorage.setItem(`bq_follow_${slug}`, next ? "1" : "0");
    const r = await fetch(`${API}/api/boutique/${slug}/follow`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ follow: next }) }).then((x) => x.json()).catch(() => null);
    if (r?.ok) { setData((d) => ({ ...d, shop: { ...d.shop, followers: r.followers } })); toast.success(next ? `Vous suivez ${shop.name}` : "Vous ne suivez plus cette boutique"); }
  };
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: shop.name, url }); } catch { /* annulé */ } }
    else { try { await navigator.clipboard.writeText(url); toast.success("Lien de la boutique copié"); } catch { toast.error("Copie impossible"); } }
  };
  const chat = () => {
    if (shop.whatsapp) window.open(`https://wa.me/${shop.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Bonjour ${shop.name}, je vous contacte depuis votre boutique AYA.`)}`, "_blank");
    else toast.info("Numéro WhatsApp non renseigné");
  };

  const sendReact = () => { fetch(`${API}/api/boutique/${slug}/live/react`, { method: "POST" }).catch(() => {}); setLive((l) => (l ? { ...l, reactions: l.reactions + 1 } : l)); };
  const sendMsg = async () => {
    if (!liveMsg.trim()) return;
    await fetch(`${API}/api/boutique/${slug}/live/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: form.name || "Client AYA", text: liveMsg }) }).catch(() => {});
    setLiveMsg("");
  };
  const submitReview = async () => {
    const r = await fetch(`${API}/api/boutique/${slug}/reviews`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order_id: done.id, customer_phone: form.phone, rating: review.rating, comment: review.comment }),
    }).then((x) => x.json()).catch(() => null);
    if (r?.ok) { setReview((p) => ({ ...p, sent: true })); setData((d) => ({ ...d, shop: { ...d.shop, rating: r.rating, reviews_count: r.reviews_count } })); toast.success("Merci pour votre avis !"); }
    else toast.error(r?.error === "already_reviewed" ? "Commande déjà notée" : "Avis impossible");
  };

  const order = async () => {
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 8) { toast.error("Indiquez votre nom et un téléphone valide"); return; }
    setPlacing(true);
    try {
      const r = await fetch(`${API}/api/boutique/${slug}/orders`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_name: form.name, customer_phone: form.phone, delivery_mode: form.mode, source: live ? "live" : "boutique", items: cart.map((l) => ({ product_id: l.product_id, qty: l.qty })) }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error("Commande impossible — un produit n'est plus disponible");
      setDone(j.order); setCart([]);
    } catch (e) { toast.error(e.message); } finally { setPlacing(false); }
  };

  if (err) return (
    <div className="bq-page" data-testid="boutique-error" style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }}>
      <Store size={40} color="#9CA3AF" />
      <b>Cette boutique n'est pas disponible</b>
      <span style={{ fontSize: 12.5, color: "#6B7280" }}>{err === "shop_not_published" ? "Elle n'est pas encore publiée." : "Vérifiez le lien."}</span>
      <button className="bq-chip on" onClick={() => navigate("/search")}>Retour à AYA</button>
    </div>
  );
  if (!shop) return <div className="bq-page" data-testid="boutique-loading" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Chargement…</div>;

  const nameParts = shop.name.split(" ");
  return (
    <div className="bq-page" data-testid="boutique-page">
      <div className="bq-top">
        <button className="bq-iconbtn" onClick={() => navigate(-1)} data-testid="boutique-back"><ArrowLeft size={20} /></button>
        <span className="ayalogo">AYA</span>
        <button className="search" onClick={() => navigate("/search")} data-testid="boutique-searchbar"><Search size={14} /> Rechercher sur AYA</button>
        <button className="bq-iconbtn" onClick={() => count && setDrawer(true)} data-testid="boutique-cart-icon">
          <ShoppingCart size={20} />{count > 0 && <span className="bq-cartbadge" data-testid="boutique-cart-badge">{count}</span>}
        </button>
        <span className="bq-iconbtn"><User size={20} /></span>
      </div>

      <div className="bq-hero" data-testid="boutique-hero">
        {shop.banner_image && <img className="bg" src={shop.banner_image} alt={shop.name} />}
        <div className="ovl" />
        <div className="inner">
          <h1>{nameParts[0]} {nameParts.length > 1 && <span className="accent">{nameParts.slice(1).join(" ")}</span>}</h1>
          {shop.city && <span className="loc"><MapPin size={12} /> {shop.city}</span>}
          {shop.tagline && <p className="tag">{shop.tagline}</p>}
          {shop.highlights?.length > 0 && (
            <div className="hl">{shop.highlights.map((h) => { const I = HL_ICONS[h.icon] || Leaf; return <span key={h.label}><I size={13} color="#7CE3A5" /> {h.label}</span>; })}</div>
          )}
        </div>
        {shop.brand_badge && <span className="badge">{shop.brand_badge}</span>}
      </div>

      <div className="bq-id" data-testid="boutique-identity">
        <div className="row1">
          <div className="logowrap">
            {shop.logo_image ? <img className="logo" src={shop.logo_image} alt="" /> : <span className="logo" style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#E8F8EF", fontSize: 26 }}>🏪</span>}
            <span className="online">En ligne</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <h2>{shop.name}</h2>
            {shop.rating ? <div className="rating">★ {String(shop.rating).replace(".", ",")} <em>({shop.reviews_count} avis)</em> · <em><Users size={11} style={{ verticalAlign: -1 }} /> {shop.followers}</em></div> : <div className="rating"><em>Nouvelle boutique</em> · <em>{shop.followers} abonnés</em></div>}
            {shop.categories?.length > 0 && <div className="cats">{shop.categories.slice(0, 3).join(" · ")}</div>}
          </div>
        </div>
        <div className="meta">
          {shop.hours && <div><Clock size={14} color="#0E8A4F" /><span><b>{shop.hours.split("·")[0]}</b>{shop.hours.split("·")[1] || ""}</span></div>}
          {shop.city && <div><MapPin size={14} color="#0E8A4F" /><span><b>{shop.city.split(",")[0]}</b>{shop.city.split(",")[1] || ""}</span></div>}
          {shop.phone_display && <a href={`tel:+225${shop.phone_display.replace(/\D/g, "")}`} style={{ textDecoration: "none" }}><div><PhoneCall size={14} color="#0E8A4F" /><span><b>{shop.phone_display}</b>Appeler</span></div></a>}
        </div>
        <div className="bq-actions">
          <button className={`bq-act ${followed ? "on" : ""}`} onClick={follow} data-testid="boutique-follow"><Heart size={17} fill={followed ? "#0E8A4F" : "none"} /> {followed ? "Suivi" : "Suivre"}</button>
          <button className="bq-act" onClick={share} data-testid="boutique-share"><Share2 size={17} /> Partager</button>
          <button className="bq-act" onClick={chat} data-testid="boutique-chat"><MessageCircle size={17} color="#25D366" /> Discuter</button>
        </div>
      </div>

      {/* LIVE en cours — produit épinglé, réactions, chat, ajout panier sans quitter le direct */}
      {live && (
        <div style={{ margin: "14px 12px 0", background: "#111827", borderRadius: 20, padding: 15, color: "#fff" }} data-testid="boutique-live">
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <span style={{ background: "#DC2626", borderRadius: 999, padding: "4px 11px", fontSize: 10.5, fontWeight: 900, letterSpacing: 0.5 }}>🔴 EN DIRECT</span>
            <b style={{ fontSize: 14 }}>{live.title}</b>
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: "#D1D5DB", fontWeight: 800 }} data-testid="live-viewers">👁 {live.viewers} · ❤️ {live.reactions}</span>
          </div>
          <p style={{ margin: "7px 0 0", fontSize: 11, color: "#9CA3AF" }}>Le vendeur est en direct (interactions et commandes en temps réel — diffusion vidéo à venir).</p>
          {live.pinned_product && (
            <div style={{ display: "flex", gap: 11, alignItems: "center", background: "#1F2937", borderRadius: 14, padding: 11, marginTop: 11 }} data-testid="live-pinned-product">
              {live.pinned_product.image && <img src={live.pinned_product.image} alt="" style={{ width: 52, height: 52, borderRadius: 11, objectFit: "cover" }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, color: "#FB923C", letterSpacing: 0.8 }}>📌 PRODUIT MIS EN AVANT</span>
                <div style={{ fontSize: 13.5, fontWeight: 900 }}>{live.pinned_product.name}</div>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#7CE3A5" }}>{fcfa(live.pinned_product.price)}</div>
              </div>
              <button className="add" onClick={() => add(live.pinned_product)} style={{ width: 38, height: 38, borderRadius: 11, border: "none", background: "#0E8A4F", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="live-add-pinned"><ShoppingCart size={16} /></button>
            </div>
          )}
          <div style={{ maxHeight: 120, overflowY: "auto", marginTop: 11, display: "grid", gap: 5 }} data-testid="live-messages">
            {live.messages.map((m) => (
              <div key={m.id} style={{ fontSize: 11.5 }}><b style={{ color: "#7CE3A5" }}>{m.name}</b> <span style={{ color: "#E5E7EB" }}>{m.text}</span></div>
            ))}
            {live.messages.length === 0 && <span style={{ fontSize: 11, color: "#6B7280" }}>Soyez le premier à écrire au vendeur…</span>}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input value={liveMsg} onChange={(e) => setLiveMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Écrire un message…" style={{ flex: 1, border: "none", outline: "none", borderRadius: 11, padding: "10px 12px", fontSize: 12.5, fontFamily: "inherit", background: "#1F2937", color: "#fff" }} data-testid="live-msg-input" />
            <button onClick={sendMsg} style={{ border: "none", borderRadius: 11, padding: "0 14px", background: "#0E8A4F", color: "#fff", fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }} data-testid="live-msg-send">Envoyer</button>
            <button onClick={sendReact} style={{ border: "none", borderRadius: 11, padding: "0 12px", background: "#1F2937", fontSize: 16, cursor: "pointer" }} aria-label="Réagir" data-testid="live-react">❤️</button>
          </div>
        </div>
      )}

      <div className="bq-chips" data-testid="boutique-category-chips">
        {["Tout", ...(shop.categories || [])].map((c) => (
          <button key={c} className={`bq-chip ${cat === c ? "on" : ""}`} onClick={() => setCat(c)} data-testid={`boutique-chip-${c}`}>{c}</button>
        ))}
      </div>

      <div className="bq-sechead">
        <h3><Flame size={18} color="#F0590B" /> Produits populaires</h3>
        <button onClick={() => setCat("Tout")} data-testid="boutique-see-all">Voir tout <ArrowRight size={13} /></button>
      </div>
      {shown.length === 0 ? (
        <div style={{ textAlign: "center", color: "#6B7280", fontSize: 12.5, padding: "18px 0" }} data-testid="boutique-empty-products">Aucun produit dans cette catégorie pour le moment.</div>
      ) : (
        <div className="bq-grid" data-testid="boutique-products">
          {shown.map((p) => (
            <div key={p.id} className="bq-prod" data-testid={`boutique-product-${p.id}`}>
              {p.image ? <img src={p.image} alt={p.name} /> : <div style={{ aspectRatio: 1, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30 }}>🛒</div>}
              <div className="body">
                <h4>{p.name}</h4>
                {p.sub && <p>{p.sub}</p>}
                <div className="foot">
                  <span className="price">{fcfa(p.price)}</span>
                  {p.in_stock ? <button className="add" onClick={() => add(p)} aria-label={`Ajouter ${p.name}`} data-testid={`boutique-add-${p.id}`}><ShoppingCart size={15} /></button> : <span className="oos">Rupture</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {shop.family_offer && (
        <div className="bq-family" data-testid="boutique-family-offer">
          <span className="tagoffer">OFFRE FAMILLE</span>
          <h3>{shop.family_offer.title}</h3>
          <div className="desc">{(shop.family_offer.desc || "").split("·").map((s) => s.trim()).filter(Boolean).map((s) => <span key={s}><CheckCircle2 size={12} color="#0E8A4F" /> {s}</span>)}</div>
          <div className="price">{fcfa(shop.family_offer.price)}</div>
          {shop.family_offer.image && <img className="ph" src={shop.family_offer.image} alt="" />}
          <div className="bullets">
            {shop.delivery?.note && <div><Truck size={15} color="#0E8A4F" /><span><b>Livraison à domicile</b>{shop.delivery.note}</span></div>}
            <div><ShieldCheck size={15} color="#0E8A4F" /><span><b>Produits 100% locaux</b>Agriculteurs ivoiriens</span></div>
            <div><Leaf size={15} color="#0E8A4F" /><span><b>Qualité et fraîcheur</b>garanties</span></div>
          </div>
          <button className="cta" onClick={addFamily} data-testid="boutique-family-order">Commander maintenant <ArrowRight size={15} /></button>
        </div>
      )}

      {/* Avis clients réels (liés aux commandes) */}
      {reviewsData && (
        <div style={{ margin: "16px 12px 0", background: "#fff", borderRadius: 18, padding: 15, boxShadow: "0 2px 10px rgba(17,24,39,0.05)" }} data-testid="boutique-reviews">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>Avis clients</h3>
            {shop.rating && <b style={{ fontSize: 13.5 }}>★ {String(shop.rating).replace(".", ",")} <span style={{ color: "#6B7280", fontWeight: 700, fontSize: 11.5 }}>({shop.reviews_count} avis)</span></b>}
          </div>
          {reviewsData.reviews.length === 0 ? (
            <p style={{ fontSize: 12, color: "#6B7280", margin: "8px 0 0" }} data-testid="reviews-empty">Les avis laissés après une commande apparaîtront ici.</p>
          ) : reviewsData.reviews.slice(0, 5).map((r) => (
            <div key={r.id} style={{ borderTop: "1px solid #F3F4F6", paddingTop: 9, marginTop: 9 }} data-testid={`review-${r.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                <b>{r.customer_name}</b>
                <span style={{ color: "#B45309", fontWeight: 900 }}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
              </div>
              {r.comment && <p style={{ margin: "3px 0 0", fontSize: 12, color: "#4B5563" }}>{r.comment}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="bq-tiles">
        <div className="bq-tile" style={{ cursor: "default" }}><span className="ic"><Truck size={17} /></span><div><b>Livraison rapide</b><span>{shop.delivery?.zone || "Selon zone du vendeur"}</span></div></div>
        <div className="bq-tile" style={{ cursor: "default" }}><span className="ic"><ShieldCheck size={17} /></span><div><b>Produits 100% locaux</b><span>Agriculteurs ivoiriens</span></div></div>
        <div className="bq-tile" style={{ cursor: "default" }}><span className="ic"><Leaf size={17} /></span><div><b>Fraîcheur garantie</b><span>Produits du jour</span></div></div>
        <button className="bq-tile" onClick={chat} data-testid="boutique-support-tile"><span className="ic"><Headphones size={17} /></span><div><b>Support vendeur</b><span>Chat sur AYA</span></div></button>
      </div>

      {count > 0 && !drawer && (
        <button className="bq-cartbar" onClick={() => setDrawer(true)} data-testid="boutique-cartbar">
          <ShoppingCart size={18} /> <b>{count} article{count > 1 ? "s" : ""}</b>
          <span className="total">{fcfa(total)} · Voir le panier</span>
        </button>
      )}

      <div className="bq-nav" data-testid="boutique-bottom-nav">
        <button onClick={() => navigate("/search")}><Home size={19} /> Accueil</button>
        <button onClick={() => navigate("/search")}><Search size={19} /> Rechercher</button>
        <button className="kiosque" onClick={() => navigate("/kiosque")}><span className="circle"><Store size={18} /></span><span className="lbl">Kiosque</span></button>
        <button onClick={() => navigate("/enregistres")}><Bookmark size={19} /> Enregistrés</button>
        <button onClick={() => navigate("/mon-compte")}><User size={19} /> Profil</button>
      </div>

      {(drawer || done) && (
        <>
          <div className="bq-overlay" onClick={() => { setDrawer(false); setDone(null); }} />
          <div className="bq-drawer" data-testid="boutique-cart-drawer">
            {done ? (
              <div className="bq-success" data-testid="boutique-order-success">
                <span className="circ"><CheckCircle2 size={32} /></span>
                <h3 style={{ marginTop: 12 }}>Commande envoyée !</h3>
                <p style={{ fontSize: 12.5, color: "#6B7280", margin: "6px 0 0" }}>
                  {shop.name} a reçu votre commande <b>n° {done.id.slice(-6).toUpperCase()}</b> d'un total de <b>{fcfa(done.total)}</b>. Vous serez contacté au numéro indiqué.
                </p>
                {!review.sent ? (
                  <div style={{ marginTop: 16, textAlign: "left", background: "#F7FBF8", borderRadius: 15, padding: 13 }} data-testid="review-widget">
                    <b style={{ fontSize: 13 }}>Notez votre expérience chez {shop.name}</b>
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} onClick={() => setReview((p) => ({ ...p, rating: n }))} style={{ background: "none", border: "none", fontSize: 26, cursor: "pointer", color: review.rating >= n ? "#F59E0B" : "#D1D5DB", padding: 0 }} aria-label={`${n} étoiles`} data-testid={`review-star-${n}`}>★</button>
                      ))}
                    </div>
                    <input value={review.comment} onChange={(e) => setReview((p) => ({ ...p, comment: e.target.value }))} placeholder="Un commentaire ? (optionnel)" style={{ width: "100%", boxSizing: "border-box", marginTop: 9, border: "1.5px solid #E5E7EB", borderRadius: 11, padding: "10px 12px", fontSize: 12.5, fontFamily: "inherit", outline: "none" }} data-testid="review-comment" />
                    <button disabled={!review.rating} onClick={submitReview} style={{ width: "100%", marginTop: 9, border: "none", borderRadius: 12, padding: 12, background: review.rating ? "#0E8A4F" : "#D1D5DB", color: "#fff", fontWeight: 900, fontSize: 13, cursor: review.rating ? "pointer" : "not-allowed", fontFamily: "inherit" }} data-testid="review-submit">Envoyer mon avis</button>
                  </div>
                ) : (
                  <div style={{ marginTop: 14, fontSize: 12.5, color: "#0E6A3D", fontWeight: 800 }} data-testid="review-thanks">✓ Merci, votre avis compte dans la note de la boutique !</div>
                )}
                <button className="bq-family cta" style={{ margin: "16px 0 0" }} onClick={() => { setDone(null); setDrawer(false); }} data-testid="boutique-order-close">Continuer mes achats</button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3>Mon panier · {shop.name}</h3>
                  <button className="bq-iconbtn" onClick={() => setDrawer(false)} data-testid="boutique-cart-close"><X size={19} /></button>
                </div>
                {cart.map((l) => (
                  <div className="bq-line" key={l.product_id} data-testid={`cart-line-${l.product_id}`}>
                    {l.image ? <img src={l.image} alt="" /> : <span style={{ width: 48, height: 48, borderRadius: 11, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>🛒</span>}
                    <div className="nm"><b>{l.name}</b><span>{fcfa(l.price)}</span></div>
                    <div className="bq-qty">
                      <button onClick={() => setQty(l.product_id, -1)} data-testid={`cart-minus-${l.product_id}`}>−</button>
                      <b data-testid={`cart-qty-${l.product_id}`}>{l.qty}</b>
                      <button onClick={() => setQty(l.product_id, 1)} data-testid={`cart-plus-${l.product_id}`}>+</button>
                    </div>
                  </div>
                ))}
                <div className="bq-totalrow"><span>Total</span><span data-testid="boutique-cart-total">{fcfa(total)}</span></div>
                <div className="bq-radio">
                  <button className={form.mode === "retrait" ? "on" : ""} onClick={() => setForm((p) => ({ ...p, mode: "retrait" }))} data-testid="cart-mode-retrait">🏪 Retrait sur place</button>
                  <button className={form.mode === "livraison" ? "on" : ""} onClick={() => setForm((p) => ({ ...p, mode: "livraison" }))} data-testid="cart-mode-livraison">🛵 Livraison{shop.delivery?.fee ? ` (+${fcfa(shop.delivery.fee)})` : ""}</button>
                </div>
                <div className="ap-field" style={{ marginTop: 12, border: "1.5px solid #E5E7EB", borderRadius: 13, display: "flex", alignItems: "center", gap: 8, padding: "0 12px" }}>
                  <input style={{ border: "none", outline: "none", width: "100%", padding: "12px 0", fontSize: 13.5, fontFamily: "inherit" }} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Votre nom" data-testid="cart-customer-name" />
                </div>
                <div style={{ marginTop: 9, border: "1.5px solid #E5E7EB", borderRadius: 13, display: "flex", alignItems: "center", gap: 8, padding: "0 12px" }}>
                  <input style={{ border: "none", outline: "none", width: "100%", padding: "12px 0", fontSize: 13.5, fontFamily: "inherit" }} value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="Votre téléphone (ex : 07 07 07 07 07)" inputMode="tel" data-testid="cart-customer-phone" />
                </div>
                <button className="bq-family cta" style={{ margin: "14px 0 0", width: "100%", justifyContent: "center" }} disabled={placing || !cart.length} onClick={order} data-testid="cart-submit-order">
                  {placing ? "Envoi…" : `Commander · ${fcfa(total)}`}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
